import api from './client';

export interface ServiceBoxFieldDiff {
  field: "reception" | "restitution";
  old: string | null;
  new: string | null;
}

export type ServiceBoxRdvState = "new" | "existing" | "modified";

export interface ServiceBoxRecentRdv {
  dossier_id: string;
  num: string;
  client_name: string;
  plate: string;
  modele: string;
  reception_date: string | null;
  reception_heure: string | null;
  reception_colonne: string | null;
  reception_personnel: string | null;
  restitution_date: string | null;
  restitution_heure: string | null;
  restitution_colonne: string | null;
  restitution_personnel: string | null;
  interventions: string[];
  status: string;
  exists_in_heric: boolean;
  heric_ordre_id: string | null;
  state: ServiceBoxRdvState;
  changed_fields: ServiceBoxFieldDiff[];
}

export interface ServiceBoxRecentRdvsResponse {
  total: number;
  new_count: number;
  existing_count: number;
  modified_count: number;
  rdvs: ServiceBoxRecentRdv[];
}

export interface ServiceBoxImportRdvResponse {
  ordre_id: string;
  dossier_id: string;
  message: string;
}

export interface ServiceBoxBatchImportResult {
  dossier_id: string;
  ordre_id: string | null;
  num: string;
  success: boolean;
  message: string;
  jobs_created: number;
}

export interface ServiceBoxBatchImportResponse {
  total: number;
  success_count: number;
  error_count: number;
  total_jobs: number;
  results: ServiceBoxBatchImportResult[];
}

export interface ServiceBoxUpdateRdvResponse {
  ordre_id: string;
  updated_fields: string[];
  jobs_resynced: number;
  message: string;
}

export interface ServiceBoxBatchUpdateResult {
  ordre_id: string;
  dossier_id: string;
  num: string;
  success: boolean;
  message: string;
  updated_fields: string[];
  jobs_resynced: number;
}

export interface ServiceBoxBatchUpdateResponse {
  total: number;
  success_count: number;
  error_count: number;
  total_jobs_resynced: number;
  results: ServiceBoxBatchUpdateResult[];
}

function rdvToPayload(rdv: ServiceBoxRecentRdv) {
  return {
    dossier_id: rdv.dossier_id,
    num: rdv.num,
    client_name: rdv.client_name,
    plate: rdv.plate,
    modele: rdv.modele,
    reception_date: rdv.reception_date,
    reception_heure: rdv.reception_heure,
    reception_colonne: rdv.reception_colonne,
    reception_personnel: rdv.reception_personnel,
    restitution_date: rdv.restitution_date,
    restitution_heure: rdv.restitution_heure,
    restitution_colonne: rdv.restitution_colonne,
    restitution_personnel: rdv.restitution_personnel,
    interventions: rdv.interventions,
  };
}

export const serviceboxSyncApi = {
  getRecentRdvs: async (days: number = 14, direction: string = "past"): Promise<ServiceBoxRecentRdvsResponse> => {
    const { data } = await api.get('/servicebox/recent-rdvs', { params: { days, direction } });
    return data;
  },

  importRdv: async (rdv: ServiceBoxRecentRdv): Promise<ServiceBoxImportRdvResponse> => {
    const { data } = await api.post('/servicebox/import-rdv', rdvToPayload(rdv));
    return data;
  },

  batchImport: async (rdvs: ServiceBoxRecentRdv[]): Promise<ServiceBoxBatchImportResponse> => {
    const { data } = await api.post('/servicebox/batch-import', {
      rdvs: rdvs.map(rdvToPayload),
    }, { timeout: 300000 }); // 5 min — atelier scraping is slow
    return data;
  },

  updateRdv: async (
    rdv: ServiceBoxRecentRdv,
    reSyncAtelier: boolean = false,
  ): Promise<ServiceBoxUpdateRdvResponse> => {
    if (!rdv.heric_ordre_id) throw new Error("RDV sans ordre Heric associé");
    const { data } = await api.post('/servicebox/update-rdv', {
      ordre_id: rdv.heric_ordre_id,
      rdv: rdvToPayload(rdv),
      re_sync_atelier: reSyncAtelier,
    }, { timeout: reSyncAtelier ? 300000 : 60000 });
    return data;
  },

  batchUpdate: async (
    rdvs: ServiceBoxRecentRdv[],
    reSyncAtelier: boolean = false,
  ): Promise<ServiceBoxBatchUpdateResponse> => {
    const payload = rdvs
      .filter((r) => r.heric_ordre_id)
      .map((r) => ({
        ordre_id: r.heric_ordre_id!,
        rdv: rdvToPayload(r),
        re_sync_atelier: reSyncAtelier,
      }));
    const { data } = await api.post('/servicebox/batch-update', {
      rdvs: payload,
    }, { timeout: 300000 });
    return data;
  },
};
